import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, ListGroup } from 'react-bootstrap';
import { Action, ActionStatus } from './QuestionComponent';
import { actionService, AcaoHistoricoResponse, AcaoHistoricoAnexoResponse } from '../../../services/actionService';
import { userService, UsuarioSimplificadoResponse } from '../../../services/userService';
import { useEntity } from '../../../contexts/EntityContext';
import './ActionModalComponent.css';



interface ActionModalComponentProps {
    action: Action | null;
    isOpen: boolean;
    onClose: () => void;
    onSaveComment: (comment: string, files: File[]) => void;
    getActionHistory: (actionId: string) => Promise<AcaoHistoricoResponse[]>;
    userPermissions?: string[];
    onActionUpdate?: (updatedAction: Action) => void;
}

// Remove the old ActionHistory interface since we're using AcaoHistoricoResponse now

const STATUS_COLUMNS: { id: ActionStatus; title: string; color: string }[] = [
    { id: 'PENDENTE', title: 'Ações Pendentes', color: '#dc3545' },
    { id: 'EM_ANDAMENTO', title: 'Em Andamento', color: '#fd7e14' },
    { id: 'AGUARDANDO_VALIDACAO', title: 'Aguardando Validação', color: '#0dcaf0' },
    { id: 'CONCLUIDA', title: 'Concluída', color: '#198754' },
    { id: 'NAO_CONCLUIDA', title: 'Não Concluída', color: '#6c757d' }
];

// Utility function to convert backend status to user-friendly text
const getStatusDisplayText = (status: ActionStatus): string => {
    const statusMap: Record<ActionStatus, string> = {
        'PENDENTE': 'Pendente',
        'EM_ANDAMENTO': 'Em Andamento',
        'AGUARDANDO_VALIDACAO': 'Aguardando Validação',
        'CONCLUIDA': 'Concluída',
        'NAO_CONCLUIDA': 'Não Concluída'
    };
    return statusMap[status] || status;
};

const ActionModalComponent: React.FC<ActionModalComponentProps> = ({
    action,
    isOpen,
    onClose,
    onSaveComment,
    getActionHistory,
    userPermissions = [],
    onActionUpdate
}) => {
    const { currentEntity } = useEntity();
    const [comment, setComment] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [historyData, setHistoryData] = useState<AcaoHistoricoResponse[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState<string | null>(null);
    const [isEditingResponsible, setIsEditingResponsible] = useState(false);
    const [newResponsibleId, setNewResponsibleId] = useState('');
    const [users, setUsers] = useState<UsuarioSimplificadoResponse[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);



    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const incoming = Array.from(e.target.files);
            setFiles(prev => {
                const existingNames = new Set(prev.map(f => f.name));
                return [...prev, ...incoming.filter(f => !existingNames.has(f.name))];
            });
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    useEffect(() => {
        const fetchHistory = async () => {
            if (!action) {
                setHistoryData([]);
                return;
            }

            try {
                setHistoryLoading(true);
                setHistoryError(null);
                const data = await getActionHistory(action.acao_id);
                setHistoryData(data);
            } catch (error) {
                console.error('Error fetching history:', error);
                setHistoryError('Erro ao carregar histórico');
                setHistoryData([]);
            } finally {
                setHistoryLoading(false);
            }
        };

        fetchHistory();
    }, [action, getActionHistory]);

    const handleAddComment = async () => {
        if (!action) return;

        try {
            const filesToSend = files.length > 0 ? files : undefined;
            await actionService.moveAction(
                action.acao_id,
                undefined,
                comment || undefined,
                filesToSend
            );

            onSaveComment(comment, files);

            const newHistoryData = await getActionHistory(action.acao_id);
            setHistoryData(newHistoryData);

            setComment('');
            setFiles([]);
        } catch (error: any) {
            console.error('Error adding comment:', error);
            // Captura erro 400 do Axios com mensagem do backend
            if (error.response && error.response.status === 400) {
                const errorMessage = error.response.data?.message || error.response.data?.error || 'Erro de validação do servidor';
                alert(`${errorMessage}`);
            } else {
                alert('Erro ao adicionar comentário. Por favor, tente novamente.');
            }
        }
    };

    const handleClose = () => {
        setComment('');
        setFiles([]);
        setIsEditingResponsible(false);
        setNewResponsibleId('');
        onClose();
    };

    const hasPermission = (permission: string): boolean => {
        return userPermissions.includes(permission);
    };

    const fetchUsers = async () => {
        if (!currentEntity) return;

        try {
            setUsersLoading(true);
            const usersData = await userService.getActiveUsersByEntity(currentEntity.entidade_id);
            setUsers(usersData);
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsers([]);
        } finally {
            setUsersLoading(false);
        }
    };

    const handleEditResponsible = () => {
        setIsEditingResponsible(true);
        setNewResponsibleId(action?.usuario_responsavel_id || '');
        fetchUsers();
    };

    const handleSaveResponsible = async () => {
        if (!action || !newResponsibleId) return;

        try {
            await actionService.updateAction(action.acao_id, {
                titulo: action.titulo,
                descricao: action.descricao,
                responsavelId: newResponsibleId
            });

            const selectedUser = users.find(user => user.usuarioId === newResponsibleId);

            const updatedActionData: Action = {
                ...action,
                usuario_responsavel_id: newResponsibleId,
                usuario_responsavel_nome: selectedUser?.nome || 'Não informado'
            };

            if (onActionUpdate) {
                onActionUpdate(updatedActionData);
            }

            setIsEditingResponsible(false);
            setNewResponsibleId('');
        } catch (error) {
            console.error('Error updating responsible:', error);
            alert('Erro ao atualizar responsável. Por favor, tente novamente.');
        }
    };

    const handleAnexoClick = async (anexoId: string) => {
        try {
            const downloadResponse = await actionService.getAnexoDownloadUrl(anexoId);
            window.open(downloadResponse.download_url, '_blank');
        } catch (error) {
            console.error('Error getting download URL:', error);
            alert('Erro ao baixar arquivo. Por favor, tente novamente.');
        }
    };

    const handleCancelEditResponsible = () => {
        setIsEditingResponsible(false);
        setNewResponsibleId('');
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Modal
            show={isOpen}
            onHide={handleClose}
            centered
            size="lg"
            dialogClassName="action-modal-fixed"
            backdrop="static"
            keyboard={true}
        >
            <Modal.Header className="action-modal-header">
                <div className="d-flex justify-content-between align-items-start w-100">
                    <div className="flex-grow-1">
                        <Modal.Title className="h6 mb-3">
                            {action?.descricao}
                        </Modal.Title>
                        <div className="text-muted fs-6 mt-1 mb-3">
                            Status Atual: <span className="status-badge" style={{
                                backgroundColor: STATUS_COLUMNS.find(col => col.id === action?.status)?.color
                            }}>
                                {getStatusDisplayText(action?.status || 'PENDENTE')}
                            </span>
                        </div>
                        {/* Responsible and Expiration Date */}
                        <div className="text-muted fs-6 mt-1">
                            {isEditingResponsible ? (
                                <div className="d-flex align-items-center">
                                    <span>Responsável: </span>
                                    <Form.Select
                                        size="sm"
                                        className="ms-2"
                                        style={{ width: 'auto' }}
                                        value={newResponsibleId}
                                        onChange={(e) => setNewResponsibleId(e.target.value)}
                                        disabled={usersLoading}
                                    >
                                        <option value="">Selecione um responsável...</option>
                                        {users.map(user => (
                                            <option key={user.usuarioId} value={user.usuarioId}>
                                                {user.nome} ({user.email})
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Button
                                        variant="success"
                                        size="sm"
                                        className="ms-2"
                                        onClick={handleSaveResponsible}
                                    >
                                        <i className="mdi mdi-check"></i>
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="ms-1"
                                        onClick={handleCancelEditResponsible}
                                    >
                                        <i className="mdi mdi-close"></i>
                                    </Button>
                                </div>
                            ) : (
                                <div className="d-flex align-items-center">
                                    <span>Responsável: {action?.usuario_responsavel_nome || 'Não informado'}</span>
                                    {hasPermission('acao.edit') && action?.status !== 'CONCLUIDA' && (
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="ms-2"
                                            onClick={handleEditResponsible}
                                        >
                                            <i className="mdi mdi-pencil"></i>
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>

                    </div>
                    <div className="text-end ms-3">
                        {action?.score !== undefined && (
                            <div className="score-display d-flex align-items-center h-100">
                                <div className="d-flex flex-column align-items-center ms-3" style={{ minWidth: 80 }}>
                                    <span className="text-dark fw-bold" style={{ fontSize: '2rem' }}>
                                        {action.score || 0}
                                    </span>
                                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>Nota Atual</small>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="action-modal-body">
                {action && (
                    <div className="mt-3">
                        <h6 className="mb-3">Histórico de Alterações</h6>
                        {historyLoading ? (
                            <div className="text-center text-muted py-4">
                                Carregando histórico...
                            </div>
                        ) : historyError ? (
                            <div className="text-center text-danger py-4">
                                {historyError}
                            </div>
                        ) : historyData.length > 0 ? (
                            <ListGroup variant="flush">
                                {historyData.map((history: AcaoHistoricoResponse) => (
                                    <ListGroup.Item key={history.acaoHistoricoId} className="history-item">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <span className="text-muted">
                                                {formatDate(history.dtCriacao)}
                                            </span>
                                            <span className="user-badge">
                                                {history.criadoPorNome}
                                            </span>
                                        </div>
                                        {history.type && (
                                            <div>
                                                <strong>Status:</strong> {getStatusDisplayText(history.type as ActionStatus)}
                                            </div>
                                        )}
                                        {history.comentario && (
                                            <div>
                                                <strong>Comentário:</strong> {history.comentario}
                                            </div>
                                        )}
                                        {history.anexos?.length > 0 && (
                                            <div>
                                                <strong>Anexos: </strong>
                                                {history.anexos.map((anexo: AcaoHistoricoAnexoResponse) => (
                                                    <span
                                                        key={anexo.anexoId}
                                                        onClick={() => handleAnexoClick(anexo.anexoId)}
                                                        className="text-decoration-none text-primary d-block"
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {anexo.anexo}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        ) : (
                            <div className="text-center text-muted py-4">
                                Nenhum histórico disponível para esta ação.
                            </div>
                        )}
                        {action?.status !== 'CONCLUIDA' && (
                            <div className="comment-section border-top pt-3">
                                <h6 className="mb-3">Adicionar Comentário</h6>
                                <Form.Group className="mb-3">
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Digite seu comentário..."
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        <small>
                                            <i className="mdi mdi-information-outline me-1"></i>
                                            Tipos de arquivo permitidos: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG. Tamanho máximo: 5MB.
                                        </small>
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                        multiple
                                        style={{ display: 'none' }}
                                    />

                                    {files.length > 0 && (
                                        <div className="file-list mb-3">
                                            {files.map((f, index) => (
                                                <div key={index} className="file-item d-flex align-items-center justify-content-between p-2 border rounded mb-2">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className="bi bi-file-earmark-text"></i>
                                                        <div>
                                                            <div className="file-name">{f.name}</div>
                                                            <small className="text-muted">{formatFileSize(f.size)}</small>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant="link"
                                                        className="p-0 text-danger"
                                                        onClick={() => handleRemoveFile(index)}
                                                    >
                                                        <i className="bi bi-x-lg"></i>
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-between align-items-center">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="d-flex align-items-center gap-2"
                                        >
                                            <i className="bi bi-paperclip"></i>
                                            Adicionar Arquivo
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleAddComment}
                                            disabled={!comment.trim() && files.length === 0}
                                        >
                                            Adicionar Comentário
                                        </Button>
                                    </div>
                                </Form.Group>
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="action-modal-footer">
                <Button variant="secondary" onClick={handleClose}>
                    Fechar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ActionModalComponent; 